<div
  data-testid="job-role-mapping-page"
  class="job-role-mapping p-4 flex flex-col gap-4"
>
  <div class="flex items-center justify-between gap-4">
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">
        {{ "configuration.job_role_mapping.title" | translate }}
      </h2>
    </div>
    <app-button
      [testId]="'job-role-mapping-refresh-btn'"
      [label]="'common.actions.refresh' | translate"
      type="secondary"
      [outlined]="true"
      icon="refresh"
      (emitOnClick)="loadData()"
    ></app-button>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-[minmax(300px,1fr)_2fr] gap-4">
    <!-- Master: Odoo job positions -->
    <div
      class="job-panel border border-gray-200 rounded-lg p-3 flex flex-col min-h-[420px] max-h-[75vh]"
    >
      <h3 class="text-sm font-semibold text-gray-700 mb-2">
        {{ "configuration.job_role_mapping.job_positions" | translate }}
      </h3>
      <span class="block w-full mb-3">
        <input
          data-testid="job-role-mapping-job-search"
          type="text"
          pInputText
          class="w-full"
          [ngModel]="searchTerm()"
          (ngModelChange)="onSearch($event)"
          [placeholder]="
            'configuration.job_role_mapping.search_placeholder' | translate
          "
        />
      </span>

      <div class="flex-1 min-h-0 flex flex-col">
        @if (loadError()) {
          <div
            class="flex-1 flex flex-col items-center justify-center text-gray-500 gap-3 py-8"
          >
            <i class="pi pi-exclamation-triangle text-3xl text-amber-500"></i>
            <span class="text-sm text-center">
              {{ "configuration.job_role_mapping.messages.load_failed" | translate }}
            </span>
            <app-button
              [testId]="'job-role-mapping-retry-btn'"
              [label]="'configuration.job_role_mapping.actions.retry' | translate"
              type="secondary"
              [outlined]="true"
              icon="refresh"
              (emitOnClick)="loadData()"
            ></app-button>
          </div>
        } @else {
          <p-table
            data-testid="job-role-mapping-job-table"
            [value]="jobs()"
            [loading]="loadingJobs()"
            selectionMode="single"
            dataKey="id"
            [selection]="selectedJob()"
            (onRowSelect)="onJobSelect($event.data)"
            [metaKeySelection]="false"
            [paginator]="jobs().length > 10"
            [rows]="10"
            [scrollable]="true"
            scrollHeight="flex"
          >
            <ng-template pTemplate="header">
              <tr>
                <th>
                  {{ "configuration.job_role_mapping.table.job_title" | translate }}
                </th>
                <th style="width: 8rem" class="text-center">
                  {{
                    "configuration.job_role_mapping.table.mapped_roles" | translate
                  }}
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-job>
              <tr
                [pSelectableRow]="job"
                [attr.data-testid]="'job-role-mapping-job-row-' + job.id"
                class="cursor-pointer hover:bg-gray-50"
              >
                <td>{{ job.name }}</td>
                <td class="text-center">
                  <p-tag
                    [value]="roleCount(job).toString()"
                    [severity]="roleCount(job) > 0 ? 'info' : 'secondary'"
                    [attr.aria-label]="
                      'configuration.job_role_mapping.table.mapped_roles_count'
                        | translate: { count: roleCount(job) }
                    "
                  ></p-tag>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="2" class="text-center text-gray-500 py-6">
                  {{ "common.table.no_data_message" | translate }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </div>
    </div>

    <!-- Detail: Keycloak roles mapped to the selected job -->
    <div
      class="detail-panel border border-gray-200 rounded-lg p-3 flex flex-col min-h-[420px] max-h-[75vh]"
    >
      @if (hasSelectedJob()) {
        <div class="flex items-center justify-between gap-2 mb-3">
          <h3 class="text-sm font-semibold text-gray-700 truncate">
            {{ "configuration.job_role_mapping.detail.mapped_roles_for" | translate }}
            <span
              class="text-blue-600"
              [pTooltip]="selectedJob()?.name || ''"
              tooltipPosition="top"
            >{{ selectedJob()?.name }}</span>
          </h3>
          <div class="flex gap-2 shrink-0">
            <app-button
              [testId]="'job-role-mapping-add-btn'"
              [label]="'configuration.job_role_mapping.actions.add_roles' | translate"
              type="info"
              icon="add"
              [disabled]="saving()"
              (emitOnClick)="openAddRolesDialog()"
            ></app-button>
            <app-button
              [testId]="'job-role-mapping-remove-btn'"
              [label]="'configuration.job_role_mapping.actions.remove' | translate"
              type="danger"
              [outlined]="true"
              icon="delete"
              [disabled]="saving() || rolesToRemove().length === 0"
              [tooltip]="
                rolesToRemove().length === 0
                  ? ('configuration.job_role_mapping.detail.remove_disabled_hint'
                    | translate)
                  : ''
              "
              (emitOnClick)="removeSelectedRoles()"
            ></app-button>
          </div>
        </div>

        @if (mappedRoleRows().length > 0) {
          <div class="flex-1 min-h-0 flex flex-col">
            <app-data-table
              data-testid="job-role-mapping-detail-table"
              [dataSource]="mappedRoleRows()"
              [columns]="mappedRoleColumns"
              [loading]="saving()"
              [hasCheckColumn]="true"
              [selection]="rolesToRemove()"
              [showPaginator]="false"
              [scrollHeight]="'flex'"
              [tableStyle]="{ width: '100%' }"
              (selectionChange)="onRemoveSelectionChange($event)"
              class="w-full"
            ></app-data-table>
          </div>
        } @else {
          <div
            class="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3 py-8"
          >
            <i class="pi pi-inbox text-3xl"></i>
            <span class="text-sm">
              {{ "configuration.job_role_mapping.detail.no_roles" | translate }}
            </span>
            <app-button
              [testId]="'job-role-mapping-add-empty-btn'"
              [label]="'configuration.job_role_mapping.actions.add_roles' | translate"
              type="info"
              icon="add"
              [disabled]="saving()"
              (emitOnClick)="openAddRolesDialog()"
            ></app-button>
          </div>
        }
      } @else {
        <div
          class="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2 py-8"
        >
          <i class="pi pi-arrow-left text-3xl"></i>
          <span class="text-sm">
            {{ "configuration.job_role_mapping.detail.select_job_hint" | translate }}
          </span>
        </div>
      }
    </div>
  </div>

  <p-toast data-testid="job-role-mapping-toast"></p-toast>
  <p-confirmDialog data-testid="job-role-mapping-confirm"></p-confirmDialog>
</div>
